import { Injectable } from '@angular/core';
import {
  MyHttpClientFactory,
  ResponseCasingEnum,
  environment,
} from '@ups/xplat/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';

/** One person in an employee picker (Sparta `EmployeePickerDto`, WI 35328). */
export interface EmployeePickerDto {
  Id: string;
  HRRef: number | null;
  Name: string;
  FullName: string;
  FirstName: string;
  MiddleName: string | null;
  LastName: string;
  Suffix: string;
  Nickname: string | null;
  Email: string | null;
  Company: number;
  IsActive: boolean;
  IsUserFavorite: boolean;
  /** US 36258: one of the named company's safety contacts (only with favoriteVpCompanyID). */
  IsRecommended?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SearchEmployeeHRRefService {
  private http: HttpClient;

  constructor(private clientFactory: MyHttpClientFactory) {
    this.http = this.clientFactory.createHttpClient(
      environment.urls.spartaAPI,
      true,
      ResponseCasingEnum.PascalCase
    );
  }

  search(
    searchString: string,
    params: HttpParams = null,
    limit: number = 20,
    favoriteType: string | null = null,
    favoriteVpCompanyID: number | null = null
  ) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    params = params || new HttpParams();
    params = params.append('searchString', searchString);
    params = params.append('take', limit);
    if (favoriteType) {
      params = params.append('favoriteType', favoriteType);
    }
    if (favoriteType && favoriteVpCompanyID != null) {
      // US 36258: the API flags this company's safety contacts as recommended
      params = params.append('favoriteVpCompanyID', favoriteVpCompanyID);
    }
    return this.http
      .get(`/api/spartaemployee/hrref/employee/dropdown`, {
        params,
      })
      .pipe(
        map((data) => {
          if (data && Array.isArray(data)) {
            // only include valid data
            return data.filter((d) => !!d.FirstName || !!d.LastName);
          }
          return data;
        })
      );
  }

  /**
   * WI 35328: one row per person (HRRef) with name, Employee `Id`, HRRef and status fields only; no SSN, Last4SSN,
   * birth date, phone or pay field (Bug 35290). Searching by an Employee id returns that record, for prefill.
   */
  searchPicker(
    searchString: string,
    params: HttpParams | null = null,
    limit = 20,
    favoriteType: string | null = null,
    favoriteVpCompanyID: number | null = null
  ) {
    params = (params || new HttpParams())
      .append('searchString', searchString)
      .append('take', limit);
    if (favoriteType) {
      params = params.append('favoriteType', favoriteType);
    }
    if (favoriteType && favoriteVpCompanyID != null) {
      // US 36258: the API flags this company's safety contacts as recommended
      params = params.append('favoriteVpCompanyID', favoriteVpCompanyID);
    }
    return this.http
      .get<EmployeePickerDto[]>(`/api/spartaemployee/hrref/employee/picker`, {
        params,
      })
      .pipe(
        map((data) =>
          Array.isArray(data)
            ? data.filter((d) => !!d.FirstName || !!d.LastName)
            : data
        )
      );
  }
}
